import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, PhoneCall, MessageSquare, DollarSign } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { voiceAPI } from '@/lib/api';

export default function VoiceUsagePage() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const r = await voiceAPI.usage(d);
      setData(r.data);
    } catch (_) { /* noop */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(days); }, [load, days]);

  const t = data?.totals || {};
  const rates = data?.rates || {};

  return (
    <div data-testid="voice-usage-page">
      <PageHeader
        title="Calling usage & cost"
        description="Per-agent call and SMS consumption for dedicated numbers, with an estimated cost (Twilio rates + 10% margin)."
        breadcrumbs={[{ label: 'Settings', href: '/platform-settings' }, { label: 'Calling', href: '/settings/voice' }, { label: 'Usage', href: '/settings/voice/usage' }]}
      />
      <div className="page-content space-y-4 max-w-5xl">
        <div className="flex items-center gap-2">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${days === d ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'}`} data-testid={`usage-range-${d}`}>Last {d}d</button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-slate-400" /></div>
        ) : !data ? null : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Card><CardContent className="p-4"><div className="text-xs text-slate-500 flex items-center gap-1"><PhoneCall size={13} /> Calls</div><div className="text-2xl font-bold text-slate-900">{t.calls || 0}</div><div className="text-xs text-slate-400">{t.minutes || 0} min</div></CardContent></Card>
              <Card><CardContent className="p-4"><div className="text-xs text-slate-500 flex items-center gap-1"><MessageSquare size={13} /> SMS sent</div><div className="text-2xl font-bold text-slate-900">{t.sms_sent || 0}</div></CardContent></Card>
              <Card><CardContent className="p-4"><div className="text-xs text-slate-500 flex items-center gap-1"><DollarSign size={13} /> Est. cost</div><div className="text-2xl font-bold text-emerald-700">${t.estimated_cost || 0}</div><div className="text-xs text-slate-400">+{rates.margin_pct}% margin</div></CardContent></Card>
              <Card><CardContent className="p-4"><div className="text-xs text-slate-500">Rates</div><div className="text-xs text-slate-600 mt-1">Voice ${rates.voice_per_min}/min</div><div className="text-xs text-slate-600">SMS ${rates.sms_per_segment}/seg</div></CardContent></Card>
            </div>

            <Card>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm" data-testid="usage-table">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-4 py-2.5">Agent</th>
                      <th className="text-left px-4 py-2.5">Number(s)</th>
                      <th className="text-right px-4 py-2.5">Calls</th>
                      <th className="text-right px-4 py-2.5">Minutes</th>
                      <th className="text-right px-4 py-2.5">SMS sent</th>
                      <th className="text-right px-4 py-2.5">SMS recv</th>
                      <th className="text-right px-4 py-2.5">Est. cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.agents.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No dedicated numbers assigned yet.</td></tr>}
                    {data.agents.map((a) => (
                      <tr key={a.user_id} className="border-t border-slate-100" data-testid={`usage-row-${a.user_id}`}>
                        <td className="px-4 py-2.5 font-medium text-slate-900">{a.name || a.user_id}</td>
                        <td className="px-4 py-2.5 text-slate-500 text-xs">{(a.numbers || []).join(', ')}</td>
                        <td className="px-4 py-2.5 text-right">{a.calls}</td>
                        <td className="px-4 py-2.5 text-right">{a.minutes}</td>
                        <td className="px-4 py-2.5 text-right">{a.sms_sent}</td>
                        <td className="px-4 py-2.5 text-right">{a.sms_received}</td>
                        <td className="px-4 py-2.5 text-right font-semibold text-emerald-700">${a.estimated_cost}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
